/**
 * A pack page asking for video (PRI-1867). The page runs sandboxed in an
 * opaque origin: no media source it can load, no popups. So on a
 * double-click it posts priori.video ({frame}) or priori.playlist
 * ({clips: [{start_frame}, ...]}) to its parent, and every host that frames
 * a pack page answers here — the viewer by opening the match video, the
 * match view by seeking the video already beside it.
 */
import { useCallback, useEffect, useRef, useState, type RefObject } from 'react'

import { api } from '../lib/api'
import { pathSeg } from '../lib/urls'
import { pageWithViewAs, viewAsGrant } from '../lib/viewAs'

/** The tracking frame a pack page's video request starts at, or null for
 *  anything else: priori.video carries `frame`, priori.playlist a list of
 *  `clips` that opens on the first one's `start_frame`. */
export function videoFrameOf(data: unknown): number | null {
  const d = data as { type?: unknown; frame?: unknown; clips?: unknown } | null
  if (!d || typeof d !== 'object') return null
  const f = d.type === 'priori.video' ? d.frame
    : d.type === 'priori.playlist' && Array.isArray(d.clips)
      ? (d.clips[0] as { start_frame?: unknown } | undefined)?.start_frame
      : undefined
  return typeof f === 'number' && Number.isInteger(f) && f >= 0 ? f : null
}

/** The bare match-video page, opened at a tracking frame. */
export function matchVideoUrl(gameCode: string, frame: number): string {
  // its own window: it adopts this tab's view-as on boot (lib/viewAs)
  return pageWithViewAs(`/match-room/${pathSeg(gameCode)}?view=video&frame=${frame}`)
}

/** The window the match video opens in: one per game AND per grant. A
 *  reused window keeps the grant it adopted on boot (its own
 *  sessionStorage), and an address without ?view_as= does not take it
 *  away — so after a stop or a club switch the same window would go on
 *  viewing as before. A digest, not the grant: any page later loaded in
 *  that window can read window.name. */
export function matchVideoWindowName(gameCode: string): string {
  const grant = viewAsGrant()
  if (!grant) return `match-viewer-${gameCode}`
  let h = 5381
  for (let i = 0; i < grant.length; i++) h = ((h * 33) ^ grant.charCodeAt(i)) >>> 0
  return `match-viewer-${gameCode}-${h.toString(36)}`
}

/** THE way to pop the match viewer out into its own window — every caller
 *  (pack pages, the Analytics workspace, the Insights workshop) comes
 *  through here, so none can reuse a window holding another view-as: the
 *  address carries this tab's grant (pageWithViewAs) and the window is
 *  named per game AND grant (matchVideoWindowName). `key` names the game.
 *  Null when the browser blocked it. */
export function openViewerWindow(url: string, key: string): Window | null {
  return window.open(pageWithViewAs(url), matchVideoWindowName(key),
                     'width=1100,height=920,resizable=yes')
}

/** The match video at a frame in its own window — one per game and
 *  view-as, reused. Null when the browser blocked it. */
export function openMatchVideo(gameCode: string, frame: number): Window | null {
  return openViewerWindow(matchVideoUrl(gameCode, frame), gameCode)
}

/** Calls `onFrame` for each video request from the page in `frameRef`.
 *  Only that frame's messages count: its origin is opaque ('null'), so
 *  the source window is the one thing that identifies it. */
export function usePackVideoRequests(
  frameRef: RefObject<HTMLIFrameElement | null>,
  onFrame: ((frame: number) => void) | undefined,
) {
  useEffect(() => {
    if (!onFrame) return
    const onMessage = (e: MessageEvent) => {
      if (!frameRef.current || e.source !== frameRef.current.contentWindow) return
      const frame = videoFrameOf(e.data)
      if (frame != null) onFrame(frame)
    }
    window.addEventListener('message', onMessage)
    return () => window.removeEventListener('message', onMessage)
  }, [frameRef, onFrame])
}

/** A pack page asking for its match video's source: it has no session, so
 *  the host answers with the presigned URL the game endpoint already
 *  serves the match room (one object, time-limited) — the page's CSP
 *  admits media from R2 only (insight_packs_api.pack_page_csp). The page
 *  asks again when its source fails, so an expired URL is replaced. */
export const VIDEO_SRC_REQUEST = 'priori.video-src-request'
export const VIDEO_SRC = 'priori.video-src'

/** The URL a sandboxed page can play: an absolute https one. The game
 *  endpoint falls back to the app's proxied route, which needs the session
 *  the page does not have — that is no source at all. */
export function playableVideoUrl(url: unknown): string | null {
  return typeof url === 'string' && /^https:\/\//.test(url) ? url : null
}

/** Answers the page in `frameRef` with `gameCode`'s video source, and says
 *  whether the page has asked — a page that plays inline must not also get
 *  the match video window on each moment (usePackVideoRequests). */
export function usePackVideoSource(
  frameRef: RefObject<HTMLIFrameElement | null>,
  gameCode: string | null | undefined,
  fetchSource: (gameCode: string) => Promise<string | null> = gameVideoSource,
): RefObject<boolean> {
  const asked = useRef(false)
  useEffect(() => {
    asked.current = false
    if (!gameCode) return
    const onMessage = (e: MessageEvent) => {
      const win = frameRef.current?.contentWindow
      if (!win || e.source !== win) return
      if ((e.data as { type?: unknown } | null)?.type !== VIDEO_SRC_REQUEST) return
      asked.current = true
      void fetchSource(gameCode).catch(() => null).then((url) => {
        // '*': the sandboxed page's origin is opaque; the URL is already
        // bound to this one object and expires
        win.postMessage({ type: VIDEO_SRC, url: playableVideoUrl(url) }, '*')
      })
    }
    window.addEventListener('message', onMessage)
    return () => window.removeEventListener('message', onMessage)
  }, [frameRef, gameCode, fetchSource])
  return asked
}

/** The game's presigned video URL, from the endpoint the match room reads. */
export async function gameVideoSource(gameCode: string): Promise<string | null> {
  const d = await api<{ game?: { video_url?: string | null } }>(
    `/api/v1/game-match-summary/game/${pathSeg(gameCode)}`)
  return d.game?.video_url ?? null
}

/** How long a moment waits before the match video window opens: a page
 *  that plays the video itself asks for its source (VIDEO_SRC_REQUEST),
 *  and pages built before the pack fix sent that AFTER their first
 *  moment — so the window opened beside the page's own player. Message
 *  order between the two is not a contract; this grace is.
 *
 *  Popups: a window.open from this handler already runs outside the click
 *  (the click is in the frame; the message arrives here after it). Chrome
 *  and Firefox keep the click's activation ~5 s and pass it to the parent
 *  page; Safari ~1 s — so 300 ms stays inside every one of them. Where the
 *  open is blocked all the same, the viewer shows its "Open the video"
 *  link. */
export const MOMENT_GRACE_MS = 300

/** The match video window for moments from the page in `frameRef`, unless
 *  the page plays the video itself (`playsInline`, usePackVideoSource) —
 *  decided MOMENT_GRACE_MS after the moment, so a source request that
 *  arrives just after it still counts. One pending open at a time: a newer
 *  moment replaces it, so one moment never opens two windows. `cancel`
 *  drops a pending open (call it when the frame loads again); unmount and
 *  a change of game drop it too. `blockedVideo`: the address to offer as a
 *  link when the browser blocked the window, else null. */
export function useMatchVideoWindow(
  frameRef: RefObject<HTMLIFrameElement | null>,
  gameCode: string | null | undefined,
  playsInline: RefObject<boolean>,
): { blockedVideo: string | null; cancel: () => void } {
  const [blockedVideo, setBlockedVideo] = useState<string | null>(null)
  const pending = useRef<number | null>(null)
  const cancel = useCallback(() => {
    if (pending.current !== null) window.clearTimeout(pending.current)
    pending.current = null
  }, [])
  useEffect(() => cancel, [gameCode, cancel])
  const onFrame = useCallback((frame: number) => {
    if (!gameCode || playsInline.current) return
    cancel()
    pending.current = window.setTimeout(() => {
      pending.current = null
      if (playsInline.current) return
      setBlockedVideo(openMatchVideo(gameCode, frame)
        ? null : matchVideoUrl(gameCode, frame))
    }, MOMENT_GRACE_MS)
  }, [gameCode, playsInline, cancel])
  usePackVideoRequests(frameRef, onFrame)
  return { blockedVideo, cancel }
}
